import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { capitalize } from 'lodash';
import { z } from 'zod';

import { DatePicker } from '@/components/ui/DatePicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

import {
    ProjectTask,
    ProjectTaskParty,
    ProjectTaskPriority,
    ProjectTaskStatus,
} from '../types';
import { SelectOption } from './CreateTaskDialog';

export const editTaskFormSchema = z
    .object({
        status: z.nativeEnum(ProjectTaskStatus, {
            error: 'Status is required',
        }),
        assigneeId: z.string().min(1, 'Assignee is required'),
        priority: z.nativeEnum(ProjectTaskPriority, {
            error: 'Priority is required',
        }),
        startDate: z.date({
            error: 'Start date is required',
        }),
        endDate: z.date({
            error: 'End date is required',
        }),
    })
    .refine((data) => data.startDate <= data.endDate, {
        message: 'Start date must be on or before end date',
        path: ['startDate'],
    })
    .refine((data) => data.endDate >= data.startDate, {
        message: 'End date must be on or after start date',
        path: ['endDate'],
    });

export type EditTaskFormValues = z.infer<typeof editTaskFormSchema>;

interface Props {
    isOpen: boolean;
    toggleOpen: () => void;
    currentTask: ProjectTask;
    onSubmit: (data: EditTaskFormValues) => void;
    persons: SelectOption[];
    statuses: SelectOption[];
    priorities: SelectOption[];
}

export function EditTaskDialog({
    isOpen,
    toggleOpen,
    currentTask,
    onSubmit,
    persons,
    statuses,
    priorities,
}: Props) {
    const form = useFormContext<EditTaskFormValues>();
    const assigneeId = form.watch('assigneeId');

    const assigneeLabel = useMemo(
        () =>
            persons.find((p) => p.value === assigneeId)?.label ?? 'Unassigned',
        [persons, assigneeId],
    );

    const getPartyBadgeColor = (party: ProjectTaskParty): string => {
        switch (party) {
            case ProjectTaskParty.Lessor:
                return 'border-blue-300 text-blue-700 bg-blue-50';
            case ProjectTaskParty.CAMO:
                return 'border-emerald-300 text-emerald-700 bg-emerald-50';
            case ProjectTaskParty.MRO:
                return 'border-amber-300 text-amber-700 bg-amber-50';
            default:
                return 'border-gray-300 text-gray-700 bg-gray-50';
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={toggleOpen}>
            <DialogContent
                className="max-w-2xl sm:max-w-lg"
                data-testid="EditTaskDialog"
            >
                <DialogHeader>
                    <DialogTitle>Edit Task</DialogTitle>
                    <DialogDescription>
                        Update the task details including dates, status, and
                        priority.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="flex flex-col gap-4 py-4">
                        <Card>
                            <CardContent className="pt-6 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <Badge
                                        variant="outline"
                                        className={getPartyBadgeColor(
                                            currentTask.party,
                                        )}
                                    >
                                        {capitalize(currentTask.party)}
                                    </Badge>
                                    <span
                                        className="text-sm font-semibold text-slate-900"
                                        data-testid="EditTaskDialog__taskName"
                                    >
                                        {currentTask.name}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-slate-600">
                                    <span>{assigneeLabel}</span>
                                </div>
                            </CardContent>
                        </Card>
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        onValueChange={field.onChange}
                                        value={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger data-testid="EditTaskDialog__status">
                                                <SelectValue placeholder="Select status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {statuses.map((status) => (
                                                <SelectItem
                                                    key={status.value}
                                                    value={status.value}
                                                >
                                                    {status.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex gap-4">
                            <FormField
                                control={form.control}
                                name="assigneeId"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Assignee</FormLabel>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger data-testid="EditTaskDialog__assignee">
                                                    <SelectValue placeholder="Select person" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {persons.map((person) => (
                                                    <SelectItem
                                                        key={person.value}
                                                        value={person.value}
                                                    >
                                                        {person.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="priority"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Priority</FormLabel>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger data-testid="EditTaskDialog__priority">
                                                    <SelectValue placeholder="Select priority" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {priorities.map((priority) => (
                                                    <SelectItem
                                                        key={priority.value}
                                                        value={priority.value}
                                                    >
                                                        {priority.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="flex gap-4">
                            <FormField
                                control={form.control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Start Date</FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                className="w-full"
                                                date={field.value}
                                                onDateChange={field.onChange}
                                                testId="EditTaskDialog__start-date"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="endDate"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>End Date</FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                className="w-full"
                                                date={field.value}
                                                onDateChange={field.onChange}
                                                testId="EditTaskDialog__end-date"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={toggleOpen}
                            data-testid="EditTaskDialog__closeButton"
                        >
                            Close
                        </Button>
                        <Button
                            type="submit"
                            data-testid="EditTaskDialog__saveButton"
                        >
                            Save
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
